import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import './recreate-image-tile.css';

const ImageTile = ({
  imageUrl,
  fallbackImageUrl,
  title,
  shotCount,
  className,
  interactive,
  badgeLabel,
  badgeIconClassName,
  metaLabel,
  matchLabel,
  overlay,
  flipOverlayAtViewportEdge,
  detachOverlayFromColumn,
  onDetails,
}) => {
  const [isOverlayFlipped, setIsOverlayFlipped] = useState(false);
  const [detachedOverlayStyle, setDetachedOverlayStyle] = useState(null);
  const detachedOverlayTimerRef = useRef(null);
  const rootClassName = `recreate-image-tile${className ? ` ${className}` : ''}${
    interactive ? ' is-interactive' : ''
  }${isOverlayFlipped ? ' is-overlay-flipped' : ''}`;

  const handleDetails = (event) => {
    if (!onDetails) {
      return;
    }
    if (event) {
      event.preventDefault();
    }
    onDetails();
  };

  const handleKeyDown = (event) => {
    if (!interactive || !onDetails) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      handleDetails(event);
    }
  };

  const updateOverlayPlacement = useCallback((event) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (detachedOverlayTimerRef.current) {
      window.clearTimeout(detachedOverlayTimerRef.current);
      detachedOverlayTimerRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    if (flipOverlayAtViewportEdge) {
      const estimatedMenuHeight = 360;
      setIsOverlayFlipped(window.innerHeight - rect.bottom < estimatedMenuHeight);
    }

    if (detachOverlayFromColumn) {
      setDetachedOverlayStyle({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [detachOverlayFromColumn, flipOverlayAtViewportEdge]);

  const resetOverlayPlacement = useCallback(() => {
    if (flipOverlayAtViewportEdge) {
      setIsOverlayFlipped(false);
    }
    if (detachOverlayFromColumn) {
      if (typeof window === 'undefined') {
        setDetachedOverlayStyle(null);
        return;
      }
      if (detachedOverlayTimerRef.current) {
        window.clearTimeout(detachedOverlayTimerRef.current);
      }
      detachedOverlayTimerRef.current = window.setTimeout(() => {
        setDetachedOverlayStyle(null);
        detachedOverlayTimerRef.current = null;
      }, 160);
    }
  }, [detachOverlayFromColumn, flipOverlayAtViewportEdge]);

  const keepDetachedOverlayOpen = useCallback(() => {
    if (detachedOverlayTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(detachedOverlayTimerRef.current);
      detachedOverlayTimerRef.current = null;
    }
  }, []);

  const renderDetachedOverlay = () => {
    if (
      !detachOverlayFromColumn
      || !detachedOverlayStyle
      || !overlay
      || typeof document === 'undefined'
    ) {
      return null;
    }

    return ReactDOM.createPortal(
      <div
        className="recreate-image-tile recreate-image-tile--portal"
        style={detachedOverlayStyle}
        onMouseEnter={keepDetachedOverlayOpen}
        onMouseLeave={resetOverlayPlacement}
      >
        {overlay}
      </div>,
      document.body,
    );
  };

  return (
    <>
      <article
        className={rootClassName}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? handleDetails : undefined}
        onKeyDown={interactive ? handleKeyDown : undefined}
        onMouseEnter={updateOverlayPlacement}
        onMouseLeave={resetOverlayPlacement}
        onFocus={updateOverlayPlacement}
        onBlur={resetOverlayPlacement}
      >
        <div className="recreate-image-tile__media">
          <img
            src={imageUrl || fallbackImageUrl}
            alt={title}
            loading="lazy"
            onError={(event) => {
              event.currentTarget.src = fallbackImageUrl;
            }}
          />
          {shotCount > 1 && (
            <span className="recreate-image-tile__badge">
              +{shotCount} Shots
            </span>
          )}
          {badgeLabel && (
            <span className="recreate-image-tile__badge recreate-image-tile__badge--right">
              {badgeIconClassName && <i className={badgeIconClassName} aria-hidden="true" />}
              {badgeLabel}
            </span>
          )}
          {(metaLabel || matchLabel) && (
            <div className="recreate-image-tile__meta">
              {metaLabel && <span>{metaLabel}</span>}
              {matchLabel && <strong>{matchLabel}</strong>}
            </div>
          )}
        </div>
        {detachOverlayFromColumn && detachedOverlayStyle ? null : overlay}
      </article>
      {renderDetachedOverlay()}
    </>
  );
};

ImageTile.propTypes = {
  imageUrl: PropTypes.string,
  fallbackImageUrl: PropTypes.string.isRequired,
  title: PropTypes.string,
  shotCount: PropTypes.number,
  className: PropTypes.string,
  interactive: PropTypes.bool,
  badgeLabel: PropTypes.string,
  badgeIconClassName: PropTypes.string,
  metaLabel: PropTypes.string,
  matchLabel: PropTypes.string,
  overlay: PropTypes.node,
  flipOverlayAtViewportEdge: PropTypes.bool,
  detachOverlayFromColumn: PropTypes.bool,
  onDetails: PropTypes.func,
};

ImageTile.defaultProps = {
  imageUrl: null,
  title: '',
  shotCount: 0,
  className: '',
  interactive: false,
  badgeLabel: '',
  badgeIconClassName: '',
  metaLabel: '',
  matchLabel: '',
  overlay: null,
  flipOverlayAtViewportEdge: false,
  detachOverlayFromColumn: false,
  onDetails: undefined,
};

export default ImageTile;
